import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FolderOpen, Trash2, RefreshCw } from 'lucide-react'
import { AppLayout } from '@/components/layout/AppLayout'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useHomeStore } from '@/features/home/store'
import type { GeneralInfo, Vehicle } from '@/features/home/types'
import {
  bulkDeleteSearches,
  deleteSearch,
  getSearch,
  listSearches,
  type SavedSearchSummary,
} from '@/features/home/searchesApi'

export function HistoryPage() {
  const navigate = useNavigate()
  const importSession = useHomeStore((s) => s.importSession)
  const findSessionBySavedId = useHomeStore((s) => s.findSessionBySavedId)
  const switchSession = useHomeStore((s) => s.switchSession)
  const [rows, setRows] = useState<SavedSearchSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [confirmBulk, setConfirmBulk] = useState(false)
  const [busy, setBusy] = useState(false)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await listSearches()
      setRows(list)
      setSelected((prev) => new Set([...prev].filter((id) => list.some((r) => r.id === id))))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Načtení selhalo.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleAll() {
    if (selected.size === rows.length) setSelected(new Set())
    else setSelected(new Set(rows.map((r) => r.id)))
  }

  async function onOpen(id: number) {
    const existing = findSessionBySavedId(id)
    if (existing) {
      switchSession(existing)
      navigate('/')
      return
    }
    try {
      const full = await getSearch(id)
      const data = full.data as {
        general?: GeneralInfo
        vehicles?: Vehicle[]
        nextVehicleId?: number
      }
      importSession(
        full.name,
        {
          general: data.general ?? ({} as GeneralInfo),
          vehicles: data.vehicles ?? [],
          nextVehicleId: data.nextVehicleId,
        },
        id,
      )
      navigate('/')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Otevření selhalo.')
    }
  }

  async function onDeleteOne(id: number) {
    if (!confirm('Opravdu smazat?')) return
    setBusy(true)
    try {
      await deleteSearch(id)
      await refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Smazání selhalo.')
    } finally {
      setBusy(false)
    }
  }

  async function onBulkDelete() {
    setBusy(true)
    try {
      await bulkDeleteSearches([...selected])
      setSelected(new Set())
      await refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Hromadné smazání selhalo.')
    } finally {
      setBusy(false)
      setConfirmBulk(false)
    }
  }

  return (
    <AppLayout>
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-zinc-900">Uložená vyhledávání</h1>
            <p className="text-sm text-zinc-500">Záznamy uložené do vašeho účtu.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
              <RefreshCw className={`mr-1 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Aktualizovat
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={selected.size === 0 || busy}
              onClick={() => setConfirmBulk(true)}
            >
              <Trash2 className="mr-1 h-4 w-4" />
              Smazat vybrané ({selected.size})
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm">
          {rows.length === 0 && !loading ? (
            <div className="p-12 text-center text-sm text-zinc-500">
              Žádná uložená vyhledávání. Použijte ikonu ☁ v záložce pro uložení.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-zinc-100 bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
                <tr>
                  <th className="w-10 px-3 py-2">
                    <Checkbox
                      checked={selected.size > 0 && selected.size === rows.length}
                      onCheckedChange={() => toggleAll()}
                    />
                  </th>
                  <th className="px-3 py-2 text-left">Název</th>
                  <th className="px-3 py-2 text-left">Pojistník</th>
                  <th className="px-3 py-2 text-right">Vozidla</th>
                  <th className="px-3 py-2 text-left">Aktualizováno</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50/50">
                    <td className="px-3 py-2">
                      <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggle(r.id)} />
                    </td>
                    <td className="px-3 py-2 font-medium text-zinc-900">{r.name}</td>
                    <td className="px-3 py-2 text-zinc-700">{r.pojistnik_name ?? '—'}</td>
                    <td className="px-3 py-2 text-right text-zinc-700">{r.vehicle_count}</td>
                    <td className="px-3 py-2 text-zinc-500">{formatDate(r.updated_at)}</td>
                    <td className="px-3 py-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => onOpen(r.id)}>
                        <FolderOpen className="mr-1 h-4 w-4" />
                        Otevřít
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => onDeleteOne(r.id)} disabled={busy}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Dialog open={confirmBulk} onOpenChange={(o) => !o && setConfirmBulk(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Smazat vybraná vyhledávání</DialogTitle>
            <DialogDescription>
              Opravdu smazat {selected.size} {selected.size === 1 ? 'záznam' : selected.size < 5 ? 'záznamy' : 'záznamů'}? Akce je nevratná.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmBulk(false)}>
              Zrušit
            </Button>
            <Button variant="destructive" onClick={onBulkDelete} disabled={busy}>
              Smazat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  )
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleString('cs-CZ', { dateStyle: 'short', timeStyle: 'short' })
  } catch {
    return iso
  }
}
